import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useApi } from "../hooks/useApi";
import { toImageSrc } from "../utils/ipfs";

interface NftRecord {
  tokenId: number;
  owner: string;
  metadataURI: string;
  metadataHash?: string;
  imageHash: string;
  imageURI?: string;
  mintedAt?: number;
  active: boolean;
  pendingWethFeesWei: string;
}

interface FeeClaimRecord {
  tokenId: number;
  owner: string;
  wethAmount: string;
  claimedAt: number;
  txHash: string;
}

interface TransferRecord {
  tokenId: number;
  from: string;
  to: string;
  blockNumber: number;
  txHash: string;
  logIndex: number;
}

export default function TokenDetailPage() {
  const { tokenId } = useParams();
  const { get } = useApi();
  const [nft, setNft] = useState<NftRecord | null>(null);
  const [claims, setClaims] = useState<FeeClaimRecord[]>([]);
  const [transfers, setTransfers] = useState<TransferRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!tokenId) return;
      setLoading(true); setError(null);
      try {
        const [nftRecord, feeClaims, transferHistory] = await Promise.all([
          get<NftRecord>(`/indexer/nfts/${tokenId}`),
          get<FeeClaimRecord[]>(`/indexer/nfts/${tokenId}/fee-claims`).catch(() => []),
          get<TransferRecord[]>(`/indexer/nfts/${tokenId}/transfers`).catch(() => [])
        ]);
        if (!cancelled) {
          setNft(nftRecord);
          setClaims(feeClaims);
          setTransfers(transferHistory);
        }
      } catch (e: unknown) {
        if (!cancelled) setError(e instanceof Error ? e.message : "Failed to load NFT detail");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => { cancelled = true; };
  }, [tokenId]);

  return (
    <main className="page">
      <div className="container" style={{ paddingBottom: "var(--space-24)" }}>
        <div style={{ marginBottom: "var(--space-6)" }}>
          <Link to="/gallery" className="btn btn-secondary btn-sm">← NFT Gallery</Link>
        </div>

        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: "var(--space-24)" }}><span className="spinner spinner-lg" /></div>
        ) : error || !nft ? (
          <div className="card text-center" style={{ padding: "var(--space-24)" }}>
            <div style={{ fontSize: "4rem", marginBottom: "var(--space-4)" }}>🦍</div>
            <p className="text-xl text-muted">{error ?? "NFT not found"}</p>
          </div>
        ) : (
          <div className="token-detail-grid">
            <section className="card" style={{ padding: "var(--space-5)" }}>
              {nft.imageURI ? (
                <img src={toImageSrc(nft.imageURI)} alt={`CODE APE #${nft.tokenId}`} style={{ width: "100%", aspectRatio: "1", objectFit: "cover", borderRadius: "var(--radius)", border: "1px solid var(--color-border)" }} />
              ) : (
                <div className="nft-card__image-placeholder" style={{ borderRadius: "var(--radius)", border: "1px solid var(--color-border)" }}><span>🦍</span></div>
              )}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "var(--space-4)", marginTop: "var(--space-5)" }}>
                <div>
                  <div className="text-mono text-xs" style={{ color: "var(--color-prime)" }}>TOKEN #{nft.tokenId}</div>
                  <h1 className="text-3xl">CODE APE #{nft.tokenId}</h1>
                </div>
                <span className={`badge ${nft.active ? "badge-green" : "badge-red"}`}>{nft.active ? "Active" : "Burned"}</span>
              </div>
              <div className="alert alert-warning" style={{ marginTop: "var(--space-5)" }}>
                Pending WETH fees are not guaranteed during third-party marketplace purchases; current owner can claim before settlement.
              </div>
            </section>

            <section style={{ display: "grid", gap: "var(--space-5)" }}>
              <div className="card" style={{ padding: "var(--space-5)" }}>
                <h2 className="text-2xl" style={{ marginBottom: "var(--space-4)" }}>Ownership / Fee State</h2>
                <div className="dense-kv">
                  <InfoRow label="Owner" value={nft.owner} />
                  <InfoRow label="Pending WETH" value={formatWeth(BigInt(nft.pendingWethFeesWei || "0"))} accent="weth" />
                  <InfoRow label="Minted block" value={String(nft.mintedAt ?? "unknown")} />
                  <InfoRow label="Status" value={nft.active ? "Active NFT receives future fees" : "Burned / inactive"} />
                </div>
                <div style={{ display: "flex", gap: "var(--space-3)", marginTop: "var(--space-5)", flexWrap: "wrap" }}>
                  <Link to="/fees" className="btn btn-primary">Claim WETH Fees</Link>
                  <Link to="/burn" className="btn btn-secondary">Burn / Claim CAPE</Link>
                </div>
              </div>

              <div className="card" style={{ padding: "var(--space-5)" }}>
                <h2 className="text-2xl" style={{ marginBottom: "var(--space-4)" }}>Provenance</h2>
                <div className="dense-kv">
                  <InfoRow label="Image hash" value={nft.imageHash} />
                  <InfoRow label="Metadata hash" value={nft.metadataHash ?? "not indexed"} />
                  <InfoRow label="Metadata URI" value={nft.metadataURI} />
                </div>
              </div>



              <div className="card" style={{ padding: "var(--space-5)" }}>
                <h2 className="text-2xl" style={{ marginBottom: "var(--space-4)" }}>Transfer history</h2>
                {transfers.length === 0 ? (
                  <p className="text-muted">No transfer events indexed for this NFT.</p>
                ) : (
                  <div className="nft-ledger">
                    <div className="nft-ledger__head" style={{ gridTemplateColumns: "0.7fr 1.2fr 1.2fr 1fr" }}><span>Block</span><span>From</span><span>To</span><span>Tx</span></div>
                    {transfers.map((transfer) => (
                      <div key={`${transfer.txHash}-${transfer.logIndex}`} className="nft-ledger__row" style={{ gridTemplateColumns: "0.7fr 1.2fr 1.2fr 1fr" }}>
                        <span className="text-mono">{transfer.blockNumber}</span>
                        <span className="text-mono">{shorten(transfer.from)}</span>
                        <span className="text-mono">{shorten(transfer.to)}</span>
                        <span className="text-mono">{shorten(transfer.txHash)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="card" style={{ padding: "var(--space-5)" }}>
                <h2 className="text-2xl" style={{ marginBottom: "var(--space-4)" }}>Fee claim history</h2>
                {claims.length === 0 ? (
                  <p className="text-muted">No fee claim events indexed for this NFT.</p>
                ) : (
                  <div className="nft-ledger">
                    <div className="nft-ledger__head" style={{ gridTemplateColumns: "1fr 1fr 1fr" }}><span>Block</span><span>Amount</span><span>Tx</span></div>
                    {claims.map((claim) => (
                      <div key={`${claim.txHash}-${claim.claimedAt}`} className="nft-ledger__row" style={{ gridTemplateColumns: "1fr 1fr 1fr" }}>
                        <span className="text-mono">{claim.claimedAt}</span>
                        <span className="text-mono" style={{ color: "var(--color-weth)" }}>{formatWeth(BigInt(claim.wethAmount || "0"))}</span>
                        <span className="text-mono">{shorten(claim.txHash)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </section>
          </div>
        )}
      </div>
    </main>
  );
}

function InfoRow({ label, value, accent }: { label: string; value: string; accent?: "weth" }) {
  return (
    <div className="kv-row">
      <span>{label}</span>
      <strong style={{ color: accent === "weth" ? "var(--color-weth)" : undefined }}>{value}</strong>
    </div>
  );
}

function shorten(value: string): string {
  if (!value) return "—";
  return value.length > 18 ? `${value.slice(0, 8)}…${value.slice(-6)}` : value;
}

function formatWeth(wei: bigint): string {
  const scaled = wei * 1000000n / 10n ** 18n;
  return `${(Number(scaled) / 1000000).toFixed(6)} WETH`;
}
